import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  sidebarCollapsed: false,
  sidebarWidth: 256,
  canvasWidth: 560,
  canvasOpen: false,
  canvasDocument: null,
  previewDocument: null,
  // Page-aware canvas state (for HTML→PDF report editing)
  canvasPage: null, // 1-based page currently visible in the canvas
  canvasNumPages: null,
  canvasTargetPage: null, // one-shot jump request consumed by the viewer
  canvasRefreshToken: 0, // bump => refetch presigned URL + reload the viewer
  chatQuote: null, // one-shot { text, page, intent } seeded into the composer
  workspaceTourRequestId: 0,
};

function resetCanvasPageState(state) {
  state.canvasPage = null;
  state.canvasNumPages = null;
  state.canvasTargetPage = null;
}

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload;
    },
    setSidebarWidth: (state, action) => {
      state.sidebarWidth = action.payload;
    },
    setCanvasWidth: (state, action) => {
      state.canvasWidth = action.payload;
    },
    openCanvas: (state, action) => {
      state.canvasDocument = action.payload;
      state.canvasOpen = true;
      resetCanvasPageState(state);
    },
    closeCanvas: (state) => {
      state.canvasOpen = false;
      state.canvasDocument = null;
      resetCanvasPageState(state);
    },
    openPreview: (state, action) => {
      state.previewDocument = action.payload;
    },
    closePreview: (state) => {
      state.previewDocument = null;
    },
    sendToCanvas: (state) => {
      if (state.previewDocument) {
        state.canvasDocument = state.previewDocument;
        state.canvasOpen = true;
        state.previewDocument = null;
        resetCanvasPageState(state);
      }
    },
    openPreviewInCanvas: (state, action) => {
      state.canvasDocument = action.payload;
      state.canvasOpen = true;
      state.previewDocument = null;
      resetCanvasPageState(state);
    },
    setCanvasPage: (state, action) => {
      const { page, numPages } = action.payload;
      state.canvasPage = page;
      state.canvasNumPages = numPages ?? state.canvasNumPages;
    },
    applyCanvasDirective: (state, action) => {
      const { document, page } = action.payload;
      const sameDoc = state.canvasOpen && state.canvasDocument?.uuid === document.uuid;
      if (sameDoc) {
        // Already open: reload the (re-rendered) file and preserve the page.
        state.canvasRefreshToken += 1;
        state.canvasTargetPage = page ?? state.canvasPage ?? null;
      } else {
        state.canvasDocument = document;
        state.canvasOpen = true;
        state.canvasPage = null;
        state.canvasNumPages = null;
        state.canvasTargetPage = page ?? null;
      }
    },
    clearCanvasTargetPage: (state) => {
      state.canvasTargetPage = null;
    },
    // Reload the open canvas document in place (e.g. right after the agent's
    // edit_report_source re-renders its PDF), preserving the current page.
    refreshCanvasInPlace: (state) => {
      if (!state.canvasOpen) return;
      state.canvasRefreshToken += 1;
      state.canvasTargetPage = state.canvasPage ?? state.canvasTargetPage;
    },
    queueChatQuote: (state, action) => {
      state.chatQuote = action.payload;
    },
    clearChatQuote: (state) => {
      state.chatQuote = null;
    },
    startWorkspaceTour: (state) => {
      state.workspaceTourRequestId += 1;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  setSidebarWidth,
  setCanvasWidth,
  openCanvas,
  closeCanvas,
  openPreview,
  closePreview,
  sendToCanvas,
  openPreviewInCanvas,
  setCanvasPage,
  applyCanvasDirective,
  clearCanvasTargetPage,
  refreshCanvasInPlace,
  queueChatQuote,
  clearChatQuote,
  startWorkspaceTour,
} = workspaceSlice.actions;

export default workspaceSlice.reducer;

// Selectors
export const selectSidebarCollapsed = (state) => state.workspace.sidebarCollapsed;
export const selectSidebarWidth = (state) => state.workspace.sidebarWidth;
export const selectCanvasWidth = (state) => state.workspace.canvasWidth;
export const selectCanvasOpen = (state) => state.workspace.canvasOpen;
export const selectCanvasDocument = (state) => state.workspace.canvasDocument;
export const selectPreviewDocument = (state) => state.workspace.previewDocument;
export const selectCanvasPage = (state) => state.workspace.canvasPage;
export const selectCanvasNumPages = (state) => state.workspace.canvasNumPages;
export const selectCanvasTargetPage = (state) => state.workspace.canvasTargetPage;
export const selectCanvasRefreshToken = (state) => state.workspace.canvasRefreshToken;
export const selectChatQuote = (state) => state.workspace.chatQuote;
export const selectWorkspaceTourRequestId = (state) => state.workspace.workspaceTourRequestId;
